import React from 'react';

const COLORS = {
  card: '#161b22',
  border: '#30363d',
  text: '#c9d1d9',
  textMuted: '#8b949e',
  green: '#3fb950',
  amber: '#d29922',
  red: '#f85149',
};

const scoreColor = (s) => s > 0.1 ? COLORS.green : s < -0.1 ? COLORS.red : COLORS.textMuted;

const timeAgo = (ts) => {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts + (ts.includes('Z') ? '' : 'Z')).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

function ScorePill({ emoji, label, value }) {
  const color = scoreColor(value);
  const scores = [
    { key: label, val: value }
  ];
  const isExtreme = false; // set by parent
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      padding: '2px 6px', borderRadius: 8, fontSize: 10, fontFamily: 'monospace',
      background: `${color}18`, color,
    }}>
      {emoji}{value?.toFixed(2)}
    </span>
  );
}

function TierBadge({ tier }) {
  const isT1 = tier === 1;
  return (
    <span style={{
      padding: '1px 5px', borderRadius: 6, fontSize: 9, fontWeight: 700,
      background: isT1 ? '#d4a01722' : '#8b949e22',
      color: isT1 ? '#d4a017' : '#8b949e',
      border: `1px solid ${isT1 ? '#d4a01755' : '#8b949e44'}`,
    }}>
      T{tier}
    </span>
  );
}

export default function TopArticles({ top_articles, source_coverage }) {
  if (!top_articles || top_articles.length === 0) return null;

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ color: COLORS.textMuted, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
        Top Articles by Signal Strength
      </div>

      {/* Article cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
        {top_articles.map((a, i) => {
          const scores = [
            { key: 'oil', emoji: '🛢️', val: a.oil_score },
            { key: 'fed', emoji: '🏦', val: a.fed_score },
            { key: 'geo', emoji: '🌍', val: a.geo_score },
            { key: 'consumer', emoji: '👤', val: a.consumer_score },
            { key: 'earnings', emoji: '📈', val: a.earnings_score },
          ];
          const maxAbs = Math.max(...scores.map(s => Math.abs(s.val || 0)));
          const netSignal = (a.oil_score + a.fed_score + a.geo_score + a.consumer_score + a.earnings_score) / 5;
          const borderColor = netSignal > 0.05 ? COLORS.green : netSignal < -0.05 ? COLORS.red : COLORS.amber;

          return (
            <div key={a.id || i} style={{
              background: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              borderLeft: `3px solid ${borderColor}`,
              borderRadius: 6,
              padding: '12px 16px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
                <a
                  href={a.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: COLORS.text, textDecoration: 'none', fontSize: 13, fontWeight: 600, flex: 1 }}
                >
                  {a.title?.length > 80 ? a.title.substring(0, 80) + '…' : a.title}
                </a>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                  <span style={{ fontSize: 10, color: COLORS.textMuted }}>{a.source}</span>
                  <TierBadge tier={a.source_tier} />
                  <span style={{ fontSize: 10, color: COLORS.textMuted }}>{timeAgo(a.published_at || a.scraped_at)}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {scores.map(s => {
                  const isMax = Math.abs(s.val || 0) === maxAbs && maxAbs > 0;
                  return (
                    <span key={s.key} style={{ position: 'relative' }}>
                      <ScorePill emoji={s.emoji} label={s.key} value={s.val} />
                      {isMax && (
                        <span style={{
                          position: 'absolute', top: -8, right: -4,
                          fontSize: 7, fontWeight: 700, color: COLORS.amber,
                          background: `${COLORS.amber}22`, padding: '0 3px', borderRadius: 3,
                        }}>
                          MOST EXTREME
                        </span>
                      )}
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Source coverage bar chart */}
      {source_coverage && source_coverage.length > 0 && (
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 20 }}>
          <div style={{ color: COLORS.textMuted, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
            Articles per Source
          </div>
          <svg width="100%" viewBox={`0 0 500 ${source_coverage.length * 24 + 8}`} style={{ display: 'block' }}>
            {source_coverage.map((s, i) => {
              const maxCount = source_coverage[0].count || 1;
              const barW = (s.count / maxCount) * 360;
              const y = i * 24 + 4;
              const fill = s.source_tier === 1 ? '#d4a017' : '#8b949e';
              return (
                <g key={s.source}>
                  <text x="0" y={y + 14} fill={COLORS.textMuted} fontSize="10" fontFamily="monospace">
                    {s.source?.length > 16 ? s.source.substring(0, 16) + '…' : s.source}
                  </text>
                  <rect x="120" y={y + 2} width={barW} height="16" rx="3" fill={fill} opacity="0.7" />
                  <text x={125 + barW} y={y + 14} fill={COLORS.text} fontSize="10" fontFamily="monospace">
                    {s.count}
                  </text>
                </g>
              );
            })}
          </svg>
          <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 10, color: COLORS.textMuted }}>
            <span><span style={{ color: '#d4a017' }}>■</span> Tier 1</span>
            <span><span style={{ color: '#8b949e' }}>■</span> Tier 2</span>
          </div>
        </div>
      )}
    </div>
  );
}
